/**
 * Utility functions for generating breadcrumb navigation
 */

export interface BreadcrumbItem {
  label: string;
  href: string;
}

/**
 * Converts a slug part to a readable label
 * @param slugPart - The slug part to convert (e.g., "getting-started")
 * @returns A human-readable label (e.g., "Getting Started")
 */
export const slugToLabel = (slugPart: string): string =>
  slugPart
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Generates nested breadcrumb items from a slug
 * @param slug - The full slug to parse (e.g., "category/subcategory/article")
 * @param basePath - The base path to prepend to each breadcrumb href
 * @returns Array of breadcrumb items
 */
export const generateNestedBreadcrumbItems = (slug: string, basePath: string = ''): BreadcrumbItem[] => {
  const slugParts = slug.split('/').filter(Boolean);
  const items: BreadcrumbItem[] = [];

  // Build up the path progressively
  let currentPath = '';

  slugParts.forEach((part) => {
    currentPath += `/${part}`;

    const label = slugToLabel(part);

    items.push({
      label,
      href: `${basePath}${currentPath}`,
    });
  });

  return items;
};

/**
 * Generates breadcrumb items for articles with nested slugs
 * @param articleSlug - The article slug
 * @param hubPath - The hub path (defaults to resources-and-support)
 * @returns Array of breadcrumb items excluding the final article
 */
export const generateArticleBreadcrumbItems = (
  articleSlug: string,
  hubPath: string = 'resources-and-support',
): BreadcrumbItem[] => {
  const basePath = `/hub/${hubPath}`;
  const allItems = generateNestedBreadcrumbItems(articleSlug, basePath);

  // Return all items except the last one (the article itself)
  return allItems.slice(0, -1);
};
